/** @format */

import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { fetchAllDamData } from "../api-helper/damapi";
import "../Style/damchart.css";
import LoadingSpinner from "./LoadingSpinner";

export default function DamRangeChart({ damData }) {
  const [data, setData] = useState([]);
  const [range, setRange] = useState([0, 0]);

  useEffect(() => {
    if (!damData || damData.length === 0) return;

    const formatted = damData
      .map((row) => ({
        date: row.date,
        avgPrice: row.avgMCP,
      }))
      .filter(Boolean)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    setData(formatted);
    // Ξεκινάει από τον πρώτο Νοέμβριο (μήνας 11) αν υπάρχει
    let startIdx = 0;
    for (let i = 0; i < formatted.length; i++) {
      const d = new Date(formatted[i].date);
      if (d.getMonth() === 10) {
        // JS: 0=Jan, 10=Nov
        startIdx = i;
        break;
      }
    }
    setRange([startIdx, formatted.length - 1]);
  }, [damData]);

  const visibleData = data.slice(range[0], range[1] + 1);

  // Βρίσκουμε τις 1ες κάθε μήνα στα ορατά δεδομένα
  const monthStartDates = visibleData
    .filter((d) => new Date(d.date).getDate() === 1)
    .map((d) => d.date);

  return (
    <div className="chart-container">
      <div className="chart-header">DAM Avg Price (€)</div>
      {data.length === 0 ? (
        <LoadingSpinner />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={visibleData}
              margin={{ top: 48, right: 24, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="damGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#007BFF" stopOpacity={1} />
                  <stop offset="100%" stopColor="#007BFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="#2C3E50"
                strokeDasharray="3 3"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tick={{
                  fill: "#4a5568",
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: "Inter, sans-serif",
                }}
                axisLine={{ stroke: "#e2e8f0", strokeWidth: 0 }}
                minTickGap={80}
                angle={-25}
                textAnchor="end"
                height={60}
                interval="preserveStartEnd"
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("el-GR", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />

              <YAxis
                stroke="#B0BEC5"
                domain={[(dataMin) => Math.min(0), "auto"]}
                tick={{ fill: "#333", fontSize: 12 }}
                axisLine={{ stroke: "#333" }}
                tickFormatter={(val) => `€${val.toFixed(0)}`}
              />
              <Tooltip
                content={({ payload, label, active }) => {
                  if (active && payload && payload.length > 0) {
                    const price = payload[0].value;
                    const STRIKE = 89;

                    return (
                      <div
                        style={{
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          borderRadius: 8,
                          padding: 10,
                          fontSize: 13,
                          color: "#333",
                        }}
                      >
                        <div>
                          <strong>Date:</strong> {label}
                        </div>
                        <div>
                          <strong>DAM Price:</strong> €{price.toFixed(2)}
                        </div>
                        <div>
                          <strong>STRIKE Price:</strong> €{STRIKE.toFixed(2)}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />

              {monthStartDates.map((date) => (
                <ReferenceLine
                  key={date}
                  x={date}
                  stroke="#2C3E50"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              ))}

              <ReferenceLine
                y={89}
                stroke="red"
                strokeWidth={2}
                label={{
                  position: "right",
                  fill: "red",
                  fontSize: 12,
                  fontWeight: "bold",
                }}
              />

              <Area
                type="monotone"
                dataKey="avgPrice"
                stroke="#007BFF"
                fill="url(#damGradient)"
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 24, padding: "0 32px" }}>
            <Slider
              range
              min={0}
              max={Math.max(data.length - 1, 1)}
              value={range}
              onChange={setRange}
              trackStyle={[{ backgroundColor: "#007BFF", height: 10 }]} // μπλε
              handleStyle={[
                {
                  borderColor: "#94a3b8",
                  height: 15,
                  width: 15,
                  marginTop: -3,
                  backgroundColor: "#94a3b8",
                  opacity: 1,
                },
                {
                  borderColor: "#94a3b8",
                  height: 15,
                  width: 15,
                  marginTop: -3,
                  backgroundColor: "#94a3b8",
                  opacity: 1,
                },
              ]}
              railStyle={{ backgroundColor: "#1E3A8A", height: 10 }}
            />
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: "#333",
                textAlign: "center",
              }}
            >
              From {data[range[0]]?.date} to {data[range[1]]?.date}
            </div>
          </div>{" "}
        </>
      )}
    </div>
  );
}
